import { useState } from "react";
import { SendEmailDialog, type SendEmailPayload, type SendEmailAttachment } from "./SendEmailDialog";
import { emailApi, type EntityKind } from "@/lib/email";

export interface ComposeEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: EntityKind;
  entityId: number;
  defaultTo?: string;
  defaultCc?: string;
  defaultBcc?: string;
  defaultSubject?: string;
  defaultBody?: string;
  onSent?: () => void;
}

const entityLabel: Record<EntityKind, string> = {
  Quote: "Quote",
  Job: "Job",
  Invoice: "Invoice",
  Account: "Account",
  Client: "Client",
};

/**
 * Unified email composer used from every entity's Emails tab. Wraps
 * `SendEmailDialog` with the generic send path: uploads attachments to the
 * `email-outbound-tmp` store via `emailApi.uploadAttachment` and sends via
 * `POST /api/email/send` (`emailApi.send`), which threads the message against
 * the given entity. Quote/Job may later swap in their own `onSend`/upload
 * wiring, but this component only implements the generic Invoice/Account/Client path.
 */
export function ComposeEmailDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  defaultTo,
  defaultCc,
  defaultBcc,
  defaultSubject,
  defaultBody,
  onSent,
}: ComposeEmailDialogProps) {
  // Tracks blobKey -> real file name for attachments uploaded in this
  // session, since SendEmailPayload.attachmentIds only carries ids.
  const [attachmentNames, setAttachmentNames] = useState<Record<string, string>>({});

  // Generic attachment upload -> temp blob; the returned id IS the blobKey,
  // carried through to send() as ComposeAttachmentRef.blobKey.
  const uploadFromComputer = async (file: File): Promise<SendEmailAttachment> => {
    const uploaded = await emailApi.uploadAttachment(file);
    setAttachmentNames((prev) => ({ ...prev, [uploaded.blobKey]: uploaded.fileName }));
    return { id: uploaded.blobKey, name: uploaded.fileName, source: "computer" };
  };

  const handleSend = async (payload: SendEmailPayload) => {
    const res = await emailApi.send({
      entityType,
      entityId,
      to: payload.to,
      cc: payload.cc,
      bcc: payload.bcc,
      subject: payload.subject,
      bodyHtml: payload.htmlBody,
      attachments: payload.attachmentIds.map((id) => ({
        blobKey: id,
        fileName: attachmentNames[id] ?? id.split("/").pop() ?? id,
        contentType: null,
      })),
      fromCode: payload.fromCode,
    });
    if (!res.sentSuccessfully) {
      // Throwing keeps SendEmailDialog open — it only closes on resolution —
      // and SendEmailDialog surfaces the thrown message inline itself.
      throw new Error(res.errorMessage ?? "The email could not be sent.");
    }
    onSent?.();
  };

  return (
    <SendEmailDialog
      open={open}
      onOpenChange={onOpenChange}
      defaultTo={defaultTo ?? ""}
      defaultCc={defaultCc}
      defaultBcc={defaultBcc}
      defaultSubject={defaultSubject ?? `Email about ${entityLabel[entityType]} #${entityId}`}
      defaultBody={defaultBody ?? ""}
      onUploadFromComputer={uploadFromComputer}
      showFromSelect
      onSend={handleSend}
    />
  );
}
